import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { Subject } from '../../../shared/models/subject';
import { Router } from '@angular/router';
import { CategoryService } from '../../../shared/services/category.service';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { MatDialog } from '@angular/material/dialog';
import { RelationshipService } from 'app/shared/services/relationship.service';
import { SubjectLink } from 'app/shared/models/subject-link';
import { SubjectService } from 'app/shared/services/subject.service';
import { messageService } from 'app/shared/services/messages/message.module';
import { Subscription, forkJoin, of } from 'rxjs';

@Component({
  selector: 'linked-subjects-widget',
  template: `<subject-list-widget
    #subjectlist
    title="{{ title }}"
    [allowEdit]="allowEdit"
    [allowDelete]="allowDelete"
    [allowEditRelationship]="allowEditRelationship"
    [allowSelect]="false"
    [data]="data"
    (onDelete)="delete($event)"
    (onEdit)="edit($event)"
    (onChangeRelationship)="changeRelationship($event)"
    noContentMessage="{{ noContentMessage }}"
  >
  </subject-list-widget>`,
})
export class LinkedSubjectsWidgetComponent implements OnInit, OnDestroy {
  _subject: Subject;
  title: string;
  selected: any;
  allowEdit = true;
  allowDelete = false;
  allowEditRelationship = false;
  primaryRelationships: string;
  noContentMessage = 'There are no linked subjects';
  data: any[];

  private onSubjectChanged: Subscription;

  //added empty inputs to remove errors - YA 18/08/2023
  @Input() id: Number;
  @Input() subjectId: Number;
  @Input() processTypeId: Number;
  @Input() settings: any;
  @Input() set subject(val: Subject) {
    if (val) {
      this.onSubjectChanged = this.subjectService.onItemChanged.subscribe(
        (_) => {
          if (this._subject) {
            this.update(this._subject);
          }
        }
      );

      this.update(val);
    }
  }

  constructor(
    private relationshipService: RelationshipService,
    private categoryService: CategoryService,
    private subjectService: SubjectService,
    private router: Router,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.title = 'Linked Subjects';
  }

  ngOnDestroy() {
    if (this.onSubjectChanged) {
      this.onSubjectChanged.unsubscribe();
    }
  }

  edit(subjectLink) {
    const subject = subjectLink.linkedSubject;
    if (subject) {
      if (this._subject.isReferral) {
        this.router.navigateByUrl(
          `/referrals/referrals/subject/${subject.uid || 'new'}`
        );
      } else {
        this.router.navigateByUrl(
          `/subjects/${subject.subjectType ? subject.subjectType.name : ''}/${
            subject.uid || 'new'
          }`
        );
      }
    }
  }

  select(subjectLink) {
    this.selected = subjectLink;
  }

  update(subject: Subject) {
    this._subject = subject;
    if (subject) {
      this.allowEdit = !subject.archived;
      this.allowDelete = !subject.archived;
      this.allowEditRelationship = !subject.archived;

      // get primary relationship id
      if (subject.categoryId) {
        this.categoryService
          .getById(subject.categoryId)
          .subscribe((category) => {
            this.primaryRelationships = category.primaryRelationshipId;
          });
      }

      const hydrate$ = [];
      const subjectLinks = [];
      subject.subjectLinks.forEach((item) => {
        // check if has a linked subject and not already added to array of linked subjects with the same relationship
        if (
          item.linkedSubject &&
          subjectLinks.findIndex(
            (x) =>
              x.linkedSubject.id === item.linkedSubject.id &&
              x.relationshipId === item.relationshipId
          ) < 0
        ) {
          // we need to calc required subjectData.displayValues for subject in case we need them for subject summary
          hydrate$.push(
            this.subjectService.hydrate(item.linkedSubject).pipe(
              take(1),
              map((_) => {
                this.relationshipService
                  .getRelationships(
                    subject.subjectTypeId,
                    item.linkedSubject.subjectTypeId
                  )
                  .subscribe((relationships) => {
                    const subjectLink = new SubjectLink(item);
                    subjectLink.possibleRelationships = relationships.filter(
                      (i) => i.id !== item.relationshipId
                    );
                    subjectLink.primary =
                      this.primaryRelationships &&
                      (',' + this.primaryRelationships + ',').includes(
                        ',' + item.relationshipId + ','
                      );
                    subjectLinks.push(subjectLink);
                  });
              })
            )
          );
        }
      });

      // push a random observable onto the array - this ensures that we have something for the forkJoin
      hydrate$.push(of(subject));

      // use forkJoin to wait for all the observables in the hydrate$ array to complete, then set the subjectList.data
      forkJoin(hydrate$)
        .pipe(take(1))
        .subscribe((_) => {
          this.data = subjectLinks.sort((a, b) =>
            a.linkedSubject.subjectType.name.localeCompare(
              b.linkedSubject.subjectType.name || a.id - b.id
            )
          );
        });
    }
  }

  delete(subjectLink) {
    subjectLink = subjectLink || this.selected;
    const info = new MessageBoxParameters(
      'Are you sure you want to remove this link?',
      'Confirm',
      MessageBoxType.Confirm,
      true
    );
    this.dialog
      .open(MessageBoxComponent, {
        data: info,
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirm') {
          // remove subject link from subject
          this._subject.subjectLinks = this._subject.subjectLinks.filter(
            (i) => i.id !== subjectLink.id
          );
          if (
            this._subject.restricted &&
            subjectLink.linkedSubject.restricted
          ) {
            this.subjectService
              .getUserSecurity(this._subject)
              .subscribe((response) => {
                const initialUsers = response;
                this.subjectService
                  .saveAsObservable(this._subject, 'Subject Link Removed')
                  .pipe(take(1))
                  .subscribe((s) => {
                    this.subjectService
                      .getUserSecurity(s)
                      .subscribe((users) => {
                        if (
                          initialUsers &&
                          users &&
                          initialUsers.length !== users.length
                        ) {
                          messageService.info(
                            `Security restrictions inherited from this subject have been removed from ${subjectLink.linkedSubject.summary}.`,
                            'Restrictions updated.'
                          );
                        }
                      });
                  });
              });
          } else {
            this.subjectService.save(this._subject, 'Subject Link Removed');
          }
        }
      });
  }

  changeRelationship(link) {
    if (this._subject) {
      const subjectLink = this._subject.subjectLinks.find(
        (i) => i.id === link.subjectLink.id
      );
      if (subjectLink) {
        subjectLink.relationshipId = link.relationship.id;
        this.subjectService.save(this._subject, 'Relationship changed');
      }
    }
  }
}
